import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './ProjectSharingAdmin.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const BASE = `${API_BASE}/api/admin/sharing`;

interface Role { id: string; name: string; permissions: string[]; is_system: boolean }
interface RouteRule {
  id: string; method: string; path_pattern: string; resource: string | null;
  access: 'read' | 'permission' | 'owner_only'; permission: string | null; status: 'active' | 'pending' | 'stale';
}
interface TestRow { method: string; path: string; allowed: boolean; reason: string | null }

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).error || `Request failed (${res.status})`);
  return data as T;
}

const isSensitive = (r: RouteRule) => r.method !== 'GET' || /\/(export|download)(\/|$)/.test(r.path_pattern);

const ProjectSharingAdmin: React.FC = () => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rules, setRules] = useState<RouteRule[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newName, setNewName] = useState('');
  const [newPerms, setNewPerms] = useState<string[]>([]);
  const [roleEdits, setRoleEdits] = useState<Record<string, string[]>>({});
  const [ruleEdits, setRuleEdits] = useState<Record<string, { access: string; permission: string }>>({});
  const [testRoleId, setTestRoleId] = useState('');
  const [testRows, setTestRows] = useState<TestRow[] | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api<{ permissions: string[]; roles: Role[]; rules: RouteRule[] }>(`${BASE}/overview`);
      setPermissions(data.permissions);
      setRoles(data.roles);
      setRules(data.rules);
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setError(''); setNotice('');
    try { await fn(); setNotice(ok); await load(); } catch (e: any) { setError(e.message); }
  };

  const toggle = (list: string[], perm: string) => (list.includes(perm) ? list.filter(x => x !== perm) : [...list, perm]);

  const pending = useMemo(() => rules.filter(r => r.status === 'pending'), [rules]);
  const stale = useMemo(() => rules.filter(r => r.status === 'stale'), [rules]);

  const runTest = async (roleId: string) => {
    setTestRoleId(roleId);
    if (!roleId) { setTestRows(null); return; }
    try {
      const data = await api<{ routes: TestRow[] }>(`${BASE}/roles/${roleId}/test`);
      setTestRows(data.routes);
    } catch (e: any) { setError(e.message); }
  };

  return (
    <div className="admin-stack admin-stack-gap-20 psa">
      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="admin-success">{notice}</div>}

      {pending.length > 0 && (
        <div className="admin-error" role="alert">
          {pending.length} route{pending.length === 1 ? '' : 's'} need a permission — members are denied until one is set (rows highlighted below).
        </div>
      )}
      {stale.length > 0 && (
        <div className="psa-warn">{stale.length} rule{stale.length === 1 ? '' : 's'} point at routes that no longer exist.</div>
      )}

      {/* ── Roles ── */}
      <section className="admin-card">
        <h3 className="admin-section-title">Project roles</h3>
        <p className="admin-field-hint">Owner and ReadOnly are system roles and cannot be changed. Permission changes apply to all shares immediately.</p>
        <div className="psa-scroll">
          <table className="admin-table psa-table">
            <thead>
              <tr><th>Role</th>{permissions.map(p => <th key={p} className="psa-perm-col">{p.replace('can_', '').replace(/_/g, ' ')}</th>)}<th /></tr>
            </thead>
            <tbody>
              {roles.map(role => {
                const current = roleEdits[role.id] ?? role.permissions;
                const dirty = !!roleEdits[role.id];
                return (
                  <tr key={role.id}>
                    <td>{role.name}{role.is_system && <span className="psa-lock" title="System role"> 🔒</span>}</td>
                    {permissions.map(p => (
                      <td key={p} className="psa-perm-col">
                        <input
                          type="checkbox"
                          aria-label={`${role.name}: ${p}`}
                          checked={current.includes(p)}
                          disabled={role.is_system}
                          onChange={() => setRoleEdits(prev => ({ ...prev, [role.id]: toggle(current, p) }))}
                        />
                      </td>
                    ))}
                    <td className="psa-actions">
                      {!role.is_system && (
                        <>
                          <button className="admin-btn admin-btn-secondary" disabled={!dirty}
                            onClick={() => run(async () => {
                              await api(`${BASE}/roles/${role.id}`, { method: 'PUT', body: JSON.stringify({ permissions: current }) });
                              setRoleEdits(prev => { const n = { ...prev }; delete n[role.id]; return n; });
                            }, 'Role saved.')}>Save</button>
                          <button className="admin-btn admin-btn-secondary"
                            onClick={() => window.confirm(`Delete role ${role.name}?`) && run(() => api(`${BASE}/roles/${role.id}`, { method: 'DELETE' }), 'Role deleted.')}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h4 className="psa-subtitle">Add role</h4>
        <div className="psa-new-role">
          <input className="admin-input" placeholder="Role name" value={newName} onChange={e => setNewName(e.target.value)} />
          <div className="psa-checks">
            {permissions.map(p => (
              <label key={p} className="psa-check">
                <input type="checkbox" checked={newPerms.includes(p)} onChange={() => setNewPerms(toggle(newPerms, p))} /> {p}
              </label>
            ))}
          </div>
          <button className="admin-btn" disabled={newName.trim().length < 2}
            onClick={() => run(async () => {
              await api(`${BASE}/roles`, { method: 'POST', body: JSON.stringify({ name: newName.trim(), permissions: newPerms }) });
              setNewName(''); setNewPerms([]);
            }, 'Role created.')}>Create role</button>
        </div>
      </section>

      {/* ── Test as role ── */}
      <section className="admin-card">
        <h3 className="admin-section-title">Test as role</h3>
        <p className="admin-field-hint">Pick a role to see which member routes it can reach.</p>
        <select className="admin-input admin-input-md" value={testRoleId} onChange={e => runTest(e.target.value)}>
          <option value="">Select a role…</option>
          {roles.filter(r => r.name !== 'Owner').map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {testRows && (
          <div className="psa-scroll">
            <table className="admin-table psa-table">
              <thead><tr><th>Method</th><th>Route</th><th>Result</th></tr></thead>
              <tbody>
                {testRows.map(t => (
                  <tr key={`${t.method} ${t.path}`} className={t.allowed ? '' : 'psa-blocked'}>
                    <td>{t.method}</td><td className="psa-path">{t.path}</td>
                    <td>{t.allowed ? '✅ allowed' : `⛔ blocked (${t.reason})`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Route permissions ── */}
      <section className="admin-card">
        <h3 className="admin-section-title">Route permissions</h3>
        <p className="admin-field-hint">What a project member may do per route. Owners are never checked. New routes appear here as pending and stay denied until set.</p>
        <div className="psa-scroll">
          <table className="admin-table psa-table">
            <thead><tr><th>Method</th><th>Route</th><th>Status</th><th>Access</th><th /></tr></thead>
            <tbody>
              {rules.map(rule => {
                const edit = ruleEdits[rule.id] ?? { access: rule.access, permission: rule.permission ?? '' };
                const dirty = !!ruleEdits[rule.id] || rule.status === 'pending';
                const sensitive = isSensitive(rule);
                return (
                  <tr key={rule.id} className={rule.status === 'pending' ? 'psa-pending' : rule.status === 'stale' ? 'psa-stale' : ''}>
                    <td>{rule.method}</td>
                    <td className="psa-path">{rule.path_pattern}</td>
                    <td>{rule.status}</td>
                    <td className="psa-access">
                      <select className="admin-input" value={edit.access} aria-label={`Access for ${rule.method} ${rule.path_pattern}`}
                        onChange={e => setRuleEdits(prev => ({ ...prev, [rule.id]: { ...edit, access: e.target.value } }))}>
                        <option value="owner_only">Owner only</option>
                        {!sensitive && <option value="read">Any member</option>}
                        <option value="permission">Needs permission…</option>
                      </select>
                      {edit.access === 'permission' && (
                        <select className="admin-input" value={edit.permission} aria-label="Required permission"
                          onChange={e => setRuleEdits(prev => ({ ...prev, [rule.id]: { ...edit, permission: e.target.value } }))}>
                          <option value="">Choose…</option>
                          {permissions.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      )}
                    </td>
                    <td>
                      <button className="admin-btn admin-btn-secondary" disabled={!dirty || !rule.resource}
                        title={rule.resource ? undefined : 'No project resource mapping for this route'}
                        onClick={() => run(async () => {
                          await api(`${BASE}/routes/${rule.id}`, { method: 'PUT', body: JSON.stringify({ access: edit.access, permission: edit.permission || null }) });
                          setRuleEdits(prev => { const n = { ...prev }; delete n[rule.id]; return n; });
                        }, 'Rule saved.')}>Save</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ProjectSharingAdmin;
